import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  OnDestroy
} from '@angular/core';
import { ChartDataset, TooltipCallbacks } from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { isSameDay } from 'date-fns';
import { Anlegg } from 'src/app/services/forbruk/forbruk.types';
import { getDaysInMonth } from 'src/app/utils/date-utils';
import {
  labelTooltipCallback,
  titleTooltipCallback
} from '../../../../forbruk/charts/forbruk-charts-utils';
import { hasChanged } from 'src/app/utils/change-utils';
import { StromstotteService } from 'src/app/services/stromstotte/stromstotte.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-day-chart-price',
  templateUrl: './day-chart-price.component.html',
  styleUrls: ['./day-chart-price.component.scss']
})
export class DayChartPriceComponent implements OnInit, OnChanges, OnDestroy {
  @Input() valgtAnlegg: Anlegg;
  @Input() selectedDate: Date;
  @Input() isStromstotteToggled: boolean;

  dataSets: ChartDataset<'line'>[];
  labels: Date[];
  title: string;
  tooltipCallbacks: _DeepPartialObject<TooltipCallbacks<'line'>>;
  useCurrentMonth: boolean;
  stotte: number[] = [];
  showLoader: boolean = true;
  subscription: Subscription;

  constructor(
    private _stromstotteService: StromstotteService,
    private _changeDetectionRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (this.valgtAnlegg && this.selectedDate) {
      this.getCalculations();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.valgtAnlegg &&
      this.selectedDate &&
      hasChanged([changes?.valgtAnlegg, changes?.isStromstotteToggled])
    ) {
      this.getCalculations();
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  getCalculations() {
    if (
      this.isStromstotteToggled &&
      new Date(2023, 8, 1) <= this.selectedDate
    ) {
      this.stotte = [];
      this.subscription = this._stromstotteService
        .stromstotteResultGrouped$(this.valgtAnlegg.objectid, this.selectedDate)
        .subscribe({
          next: (res) => {
            if (res !== undefined) {
              const stromstotteByDay = res?.receivesTotalByGrouped || [];
              let numberOfDays: number;

              if (this.selectedDate.getMonth() === new Date().getMonth()) {
                const presentDD = new Date().getDate();
                numberOfDays = presentDD > getDaysInMonth(this.selectedDate) ? getDaysInMonth(this.selectedDate) : presentDD;
              } else {
                numberOfDays = getDaysInMonth(this.selectedDate);
              }

              this.stotte = [];
              for (let i = 0; i < numberOfDays; i++) {
                const stotteDag =
                  stromstotteByDay?.find((x) => x.date?.getDate() === i + 1)
                    ?.receives || 0;
                this.stotte.push(stotteDag);
              }
            } else {
              this.stotte = [];
            }
            this.getChartData();
            this.showLoader = false;
            this._changeDetectionRef.detectChanges();
          },
          error: () => {
            // TODO: Handle?
            this.getChartData();
            this.showLoader = false;
          }
        });
    } else {
      this.showLoader = false;
      this.getChartData();
      this._changeDetectionRef.detectChanges();
    }
  }

  getChartData() {
    this.dataSets = [];
    let maxY = 0;

    // Plusskunde produksjon
    const belopPluss = [];
    let plusskundedataFound = false;

    this.title = 'Kostnad';
    this.tooltipCallbacks = {
      label: (tt) => labelTooltipCallback(tt, 'kr'),
      title: (tt) =>
        titleTooltipCallback(tt, 'day', 'kr', 'forbruk_tooltip_chart2')
    };
    const noOfDays = getDaysInMonth(this.selectedDate);
    this.labels = [...Array(noOfDays).keys()].map(
      (n) =>
        new Date(
          this.selectedDate?.getFullYear(),
          this.selectedDate.getMonth(),
          n + 1
        )
    );

    const data = this.labels.map((date) => {
      const element = this.valgtAnlegg.verdiList.find((v) =>
        isSameDay(new Date(v.tidspunkt), date)
      );
      if (element) {
        if (element.verdi > maxY) {
          maxY = element.verdi;
        }

        if (this.valgtAnlegg.plusskunde) {
          const plussEarned = element.belopPlussverdi
            ? Math.abs(element.belopPlussverdi)
            : 0;

          if (plussEarned) {
            plusskundedataFound = true;
            belopPluss.push(plussEarned);
          } else belopPluss.push(0);

          if (plusskundedataFound && plussEarned > maxY) {
            maxY = plussEarned;
          }
        }

        return {
          kraft: element.belopKraft || 0,
          nettleie: element.belopNettleie || 0
        };
      } else {
        if (this.valgtAnlegg.plusskunde) {
          belopPluss.push(0);
        }
        return {
          kraft: 0,
          nettleie: 0
        };
      }
    });
    // Remove trailing zeros for nicer line graph
    while (
      data.map((x) => x.kraft)[data.length - 1] === 0 &&
      data.map((x) => x.nettleie)[data.length - 1] === 0
    ) {
      data.pop();
    }
    if (this.stotte.reduce((a, b) => a + b, 0) > 0) {
      let stotte: number[] = [];
      for (let i = 0; i < data.length; i++) {
        stotte.push(data[i].kraft - this.stotte[i]);
      }
      if (stotte.some((x) => !Number.isNaN(x))) {
        this.dataSets.push({
          label: 'Strøm inkl. støtte',
          pointStyle: 'line',
          fill: false,
          backgroundColor: '#002987',
          borderColor: '#002987',
          borderWidth: 2,
          pointRadius: 1,
          borderDash: [5, 5],
          data: stotte
        });
      }
    }
    this.dataSets.push({
      label: 'Strøm',
      pointStyle: 'line',
      fill: false,
      backgroundColor: '#002987',
      borderColor: '#002987',
      borderWidth: 2,
      pointRadius: 1,
      data: data.map((x) => x?.kraft || 0)
    });
    if (this.valgtAnlegg.gjennomfakturering) {
      this.dataSets.push({
        label: 'Nettleie',
        pointStyle: 'line',
        fill: false,
        backgroundColor: '#ff80ff',
        borderColor: '#ff80ff',
        borderWidth: 2,
        pointRadius: 1,
        data: data.map((x) => x?.nettleie || 0)
      });
    }
    if (plusskundedataFound) {
      this.dataSets.push({
        label: 'Inntekt Plusskunde',
        pointStyle: 'line',
        fill: false,
        backgroundColor: '#26b41b',
        borderColor: '#26b41b',
        borderWidth: 2,
        pointRadius: 1,
        data: belopPluss
      });
    }
  }
}
